import { AdvancedVideo } from "@cloudinary/react";
import { Cloudinary } from "@cloudinary/url-gen";
import { quality } from "@cloudinary/url-gen/actions/delivery";
import { fill } from "@cloudinary/url-gen/actions/resize";
import { GatsbyImage, getImage, ImageDataLike } from "gatsby-plugin-image";
import { marked } from "marked";
import * as React from "react";
import { PropsWithChildren, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {Refractor, registerLanguage} from "react-refractor";
import javascript from "refractor/lang/javascript";
import json from "refractor/lang/json";
import typescript from "refractor/lang/typescript";
import html from "refractor/lang/cshtml";

import DoNotMountComponent from "../../modules/DoNotMountComponent/DoNotMountComponent";
import IndexOfHeadings, {
  cleanEncodeAndCapitalize,
} from "../IndexOfHeadings/IndexOfHeadings";
import Markdown from "../Markdown/Markdown";
import CatchEmail from "../Organisms/CatchEmail/CatchEmail";

type SanityContent = {
  __typename:
    | "SanityCaptureEmails"
    | "SanityCode"
    | "SanityIframe"
    | "SanityMd"
    | "SanityPhoto"
    | "SanityScript"
    | "SanityTable"
    | "SanityIndexOfHeadings";
};

export type SanityContentItem =
  Queries.SanityCaptureEmailsOrCodeOrIframeOrIndexOfHeadingsOrMdOrPhotoOrScriptOrTable &
    SanityContent;

type PropsContentList = PropsWithChildren<{
  data: SanityContentItem[];
}>;

registerLanguage(json);
registerLanguage(javascript);
registerLanguage(typescript);
registerLanguage(html);

const ContentList: React.FC<PropsContentList> = (props: PropsContentList) => {
  if (!props.data || !Array.isArray(props.data) || props.data.length < 1)
    return <DoNotMountComponent sms={"ContentList"} log={props} />;
  const { t } = useTranslation("global");
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  function codePrettier(code: string): JSX.Element {
    const splitCode = code.split("\r\n");
    return (
      <code>
        {" "}
        {splitCode.map((line, index) => (
          <span key={index}>
            {line.replace(/\s/gm, " ")} <br />
          </span>
        ))}
      </code>
    );
  }
  return (
    <div className={`m-content-list`}>
      {props.data.map((_item, index) => {
        switch (_item.__typename as string) {
          case "SanityCaptureEmails": {
            const captureEmails = _item as Queries.SanityCaptureEmails;
            return (
              <CatchEmail
                key={index}
                text={captureEmails?.text}
                cta={captureEmails?.cta}
                highlight={captureEmails?.highlight}
                is_molecule={true}
              />
            );
          }
          case "SanityCode": {
            const code = _item as Queries.SanityCode;
            return (
              <div className="scroll-container">
                <div className={"scroll-container__content"}>
                  <Refractor
                    language={code.language as string}
                    value={code.code as string}
                    markers={code.highlightedLines as number[]}
                  />
                </div>
              </div>
            );
          }
          case "SanityIframe": {
            const iframeData = _item as Queries.SanityIframe;
            return (
              <div key={index} className={"content-list__iframe"}>
                <iframe
                  src={iframeData?.url as string}
                  title={`${iframeData?.title}`}
                  loading="lazy"
                  allowFullScreen
                />
              </div>
            );
          }
          case "SanityMd": {
            const md = _item as Queries.SanityMd;
            const renderer = new marked.Renderer();
            renderer.heading = ({ tokens, depth }) => {
              const text = tokens.filter(token => 'text' in token)
                  .map(token => token.text)
                  .join('');
              if (depth === 2) {
                return `<h2 id="${cleanEncodeAndCapitalize(text)}">${text}</h2>`;
              } else {
                return `<h${depth}>${text}</h${depth}>`;
              }
            };
            return (
              <Markdown key={index} markdown={md.value} renderer={renderer} />
            );
          }
          case "SanityPhoto": {
            const file = _item as Queries.SanityPhoto;
            // eslint-disable-next-line no-case-declarations
            if (file?.asset) {
              const image = getImage(file.asset as unknown as ImageDataLike);
              if (!image)
                return (
                  <span key={index}>
                    {t("No se ha podido cargar la imagen")}
                  </span>
                );
              return (
                <div className={"content-list__photo"}>
                  <GatsbyImage
                    key={index}
                    alt={`${file.alt}`}
                    title={file?.title ?? ""}
                    image={image}
                  />
                  {file?.title && (
                    <p className={"text-center m-top-0"}>
                      <small>{file.title}</small>
                    </p>
                  )}
                </div>
              );
            }
            if (file?.media?.resource_type === "video") {
              const cld = new Cloudinary({
                cloud: {
                  cloudName: process.env.GATSBY_CLOUDINARY_CLOUD_NAME || "",
                },
              });
              if (!file.media?.public_id) return <></>;
              const video = cld
                .video(file?.media?.public_id)
                .delivery(quality(80))
                .resize(fill().width(935).height(538));
              return (
                <AdvancedVideo
                  key={index}
                  cldVid={video}
                  cldPoster="auto"
                  autoPlay={false}
                  muted={false}
                  loop={false}
                  controls={true}
                  className={"content-list__video"}
                />
              );
            }
            return <span key={index}>No se ha podido cargar la foto</span>;
          }
          case "SanityScript": {
            const scriptData = _item as Queries.SanityScript;
            return (
              <div className={"video__wrapper"}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: scriptData?.code as string,
                  }}
                />
              </div>
            );
          }
          case "SanityTable": {
            const { rows } = _item as Queries.SanityTable;
            if (!rows || !Array.isArray(rows) || rows.length < 1) return <></>;
            return (
              <div key={index} className="scroll-container">
                <div className={"scroll-container__content"}>
                  <table className={"m-table"}>
                    <tbody>
                      {rows.map((row, indexRow) => (
                        <tr key={indexRow}>
                          {row.cells.map((cell: string, indexCell: number) => {
                            if (indexRow === 0) {
                              return (
                                <th className={"a-table__th"} key={indexCell}>
                                  {cell}
                                </th>
                              );
                            } else {
                              return (
                                <td className={"a-table__cell"} key={indexCell}>
                                  {cell}
                                </td>
                              );
                            }
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          }
          case "SanityIndexOfHeadings": {
            const indexOfHeadingsData = _item as Queries.SanityIndexOfHeadings;
            return (
              <div className={"index-of-heading__content"} key={index}>
                <IndexOfHeadings
                  data={{
                    title: indexOfHeadingsData.title as string,
                    indexList: props.data,
                  }}
                />
              </div>
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
};

export default ContentList;
